// Trash (/trash) — the company's soft-deleted items, restorable until their
// automatic purge (30 days after deletion). Header over a toolbar (ONE combined
// Filters dropdown with Deleted-by / Deleted-date categories + a trailing items
// count) and the common DataTable + TableFooter, all server-side via POST
// trash/list (deletedBy id array + the whenDeleted/fromDate/toDate window).
// There is NO search and NO orderBy on the wire — the server fixes
// newest-deleted first — so the toolbar ships without a search box and no
// header sorts (no inert affordances). Modeled on the activity-log module.
//
// Row selection + the two bulk actions follow the data-source entries page:
// a Map (not a Set) so the selection survives paging, one selection bar with
// Restore / Delete-permanently / Clear, each action verb-gated (`restore` is
// the trash module's extra, permanent delete its `delete` verb). Restore runs
// straight from the bar — it puts things back — while the permanent delete is
// irreversible and confirms first (the datasource split: destructive confirms,
// the rest act). Both endpoints are all-or-nothing bulk calls, so one row and
// a whole selection are the same call. A rejected restore carries a serverside
// reason worth reading (e.g. "restore the parent project first"), so its catch
// surfaces the API `msg` over the generic toast.

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Button,
  Card,
  ConfirmModal,
  DataTable,
  FilterDropdown,
  Icon,
  TableFooter,
  type FilterDropdownCategory,
  type FilterDropdownDateRange,
} from '@/components/common';
import useModuleAccess, { useCanRestoreTrash } from '@/hooks/useModuleAccess';
import useStickyTotal from '@/hooks/useStickyTotal';
import {
  TrashDeletedFilter,
  useBulkDeleteTrashMutation,
  useBulkRestoreTrashMutation,
  useGetTrashListQuery,
  type TrashRow,
} from '@/api/endpoints/trashApi';
import { useGetUsersSelectionQuery } from '@/api/endpoints/usersApi';
import { DATE_ANY } from '@/utils/dateFilterPresets';
import { trashRowName } from './trashMeta';
import { useTrashColumns } from './trashColumns';

// Combined-dropdown category keys (rail order) + the empty selection shape.
const FILTER_KEYS = ['deletedBy', 'whenDeleted'] as const;
const emptyFilters = (): Record<string, Set<string>> =>
  Object.fromEntries(FILTER_KEYS.map((k) => [k, new Set<string>()]));

// The wire's fromDate/toDate are ISO datetimes (only sent with the 'custom'
// preset); the range inputs give local yyyy-mm-dd, so each edge maps to its
// local day boundary — start-of-day / end-of-day — keeping the window
// inclusive (the activity-log idiom).
const toIsoEdge = (ymd: string | undefined, edge: 'start' | 'end'): string | undefined => {
  if (!ymd) return undefined;
  const d = new Date(`${ymd}T${edge === 'start' ? '00:00:00.000' : '23:59:59.999'}`);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
};

const TrashPage = () => {
  const { t } = useTranslation();

  // Verb gates (hidden, not disabled, when missing): `restore` is the trash
  // module's own extra, permanent delete its core `delete` verb.
  const { canDelete } = useModuleAccess('trash');
  const canRestore = useCanRestoreTrash();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(6);
  const [filters, setFilters] = useState<Record<string, Set<string>>>(emptyFilters);
  // Custom FROM/TO of the Deleted-date category (keyed 'whenDeleted').
  const [dateRanges, setDateRanges] = useState<Record<string, FilterDropdownDateRange>>({});
  // A Map, not a Set: selection persists across pages, so the bar's actions
  // need the row's own facts even once it is no longer loaded (the datasource
  // entries idiom).
  const [selected, setSelected] = useState<Map<string, TrashRow>>(new Map());
  // Pending permanent delete — `name` set only when a single row was picked,
  // so the title can say which.
  const [confirmDelete, setConfirmDelete] = useState<{ ids: string[]; name?: string } | null>(
    null,
  );

  // Deleted-by options — fetched up-front so the pane is ready on first open.
  const { data: userOptions = [] } = useGetUsersSelectionQuery();

  // "Any time" → undefined (never on the wire). The option ids are the trash
  // enum's own slugs — they differ from the forms-domain presets, so the page
  // builds its own list below rather than reusing buildDateFilterOptions.
  const picked = filters.whenDeleted.size ? [...filters.whenDeleted][0] : undefined;
  const whenDeleted =
    picked && picked !== DATE_ANY ? (picked as TrashDeletedFilter) : undefined;
  const customRange =
    whenDeleted === TrashDeletedFilter.CUSTOM ? dateRanges.whenDeleted : undefined;

  const { data, currentData, isLoading, isFetching, isError } = useGetTrashListQuery(
    {
      deletedBy: filters.deletedBy.size ? [...filters.deletedBy] : undefined,
      whenDeleted,
      fromDate: toIsoEdge(customRange?.start || undefined, 'start'),
      toDate: toIsoEdge(customRange?.end || undefined, 'end'),
      page,
      limit,
    },
    { refetchOnMountOrArgChange: true },
  );

  const [bulkRestore, { isLoading: restoring }] = useBulkRestoreTrashMutation();
  const [bulkDelete, { isLoading: deleting }] = useBulkDeleteTrashMutation();

  // A new filter narrows the set — snap back to page 1 so the pager can't sit
  // past the end of the new list.
  useEffect(() => setPage(1), [filters, dateRanges]);

  // One combined Filters trigger (the app-wide FilterDropdown idiom) — the
  // Deleted-by / Deleted-date categories share its rail.
  const filterCategories: FilterDropdownCategory[] = useMemo(
    () => [
      {
        key: 'deletedBy',
        label: t('trash_filter_deleted_by'),
        icon: 'user',
        searchable: true,
        searchPlaceholder: t('members_search_people'),
        options: userOptions.map((u) => ({ id: u.id, label: u.name })),
      },
      {
        key: 'whenDeleted',
        label: t('trash_filter_deleted_date'),
        icon: 'calendar',
        type: 'single',
        rangeOptionId: TrashDeletedFilter.CUSTOM,
        defaultOptionId: DATE_ANY,
        options: [
          { id: DATE_ANY, label: t('forms_filter_date_any'), icon: 'clock' },
          { id: TrashDeletedFilter.TODAY, label: t('text_today'), icon: 'clock' },
          {
            id: TrashDeletedFilter.LAST_7_DAYS,
            label: t('select_last_seven_days'),
            icon: 'clock',
          },
          {
            id: TrashDeletedFilter.LAST_30_DAYS,
            label: t('select_last_thirty_days'),
            icon: 'clock',
          },
          {
            id: TrashDeletedFilter.CUSTOM,
            label: t('inspection_filter_due_custom'),
            icon: 'calendar',
          },
        ],
      },
    ],
    [t, userOptions],
  );
  const columns = useTrashColumns();

  const items = data?.items ?? [];
  // The API reports the record count on page-1 responses only (absent on
  // page 2+) — record it there and keep serving it while paging.
  const total = useStickyTotal(currentData?.total, page);
  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);

  const selectedIdSet = useMemo(() => new Set(selected.keys()), [selected]);
  const onlySelected = selected.size === 1 ? [...selected.values()][0] : null;

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(id)) next.delete(id);
      else {
        const row = items.find((r) => r.id === id);
        if (row) next.set(id, row);
      }
      return next;
    });
  };

  const allSelected = items.length > 0 && items.every((r) => selected.has(r.id));
  const someSelected = !allSelected && items.some((r) => selected.has(r.id));
  const toggleSelectAll = () => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (allSelected) items.forEach((r) => next.delete(r.id));
      else items.forEach((r) => next.set(r.id, r));
      return next;
    });
  };

  // Acted-on ids leave the selection on success — the rows leave the listing
  // either way (restored items return to their modules, deleted ones are gone),
  // so leaving them ticked would keep the bar acting on phantom rows.
  const dropFromSelection = (ids: string[]) =>
    setSelected((prev) => {
      const next = new Map(prev);
      ids.forEach((id) => next.delete(id));
      return next;
    });

  /**
   * Restore runs straight from the bar — no confirmation, it puts things back.
   * The endpoint is all-or-nothing, and a rejection names the blocker ("the
   * parent project is in the trash — restore it first"), so the catch prefers
   * the server's `msg` over the generic toast: it tells the user what to do.
   */
  const runRestore = async () => {
    const ids = [...selected.keys()];
    try {
      await bulkRestore({ ids }).unwrap();
      toast.success(
        onlySelected && trashRowName(onlySelected)
          ? t('trash_restored', { name: trashRowName(onlySelected) })
          : t('trash_bulk_restored', { count: ids.length }),
      );
      dropFromSelection(ids);
    } catch (e) {
      const msg = (e as { msg?: string } | null)?.msg;
      toast.error(msg || t('error_generic'));
    }
  };

  const runDelete = async () => {
    if (!confirmDelete) return;
    const { ids, name } = confirmDelete;
    try {
      await bulkDelete({ ids }).unwrap();
      toast.success(
        name
          ? t('trash_deleted_forever', { name })
          : t('trash_bulk_deleted_forever', { count: ids.length }),
      );
      dropFromSelection(ids);
      setConfirmDelete(null);
    } catch {
      toast.error(t('error_generic'));
    }
  };

  return (
    <div className='trash-page'>
      <header className='trash-page__header'>
        <div>
          <h1 className='trash-page__title'>{t('trash_title')}</h1>
          <p className='trash-page__subtitle'>{t('trash_subtitle')}</p>
        </div>
      </header>

      <div className='trash-page__toolbar'>
        <div className='trash-page__toolbar-lead'>
          <FilterDropdown
            label={t('action_filter')}
            categories={filterCategories}
            value={filters}
            ranges={dateRanges}
            onApply={(next, ranges) => {
              setFilters(next);
              setDateRanges(ranges);
            }}
            emptyText={t('text_no_results_found')}
          />
        </div>

        <span className='trash-page__count'>{t('trash_entries_count', { count: total })}</span>
      </div>

      <Card padded={false} className='trash-page__panel'>
        {/* Count + the two verbs on the lead, Clear alone on the trail (the
            datasource bar's shape). Each verb renders only when granted, so a
            view-only role selects into nothing and gets no checkboxes at all
            (gated below on the table). */}
        {selected.size > 0 && (
          <div className='trash-page__selection-bar'>
            <div className='trash-page__selection-lead'>
              <span className='trash-page__selected'>
                {t('trash_selected_count', { count: selected.size })}
              </span>
              <span className='trash-page__selection-divider' />
              {canRestore && (
                <Button
                  variant='ghost'
                  iconStart='archive-restore'
                  onClick={runRestore}
                  loading={restoring}
                >
                  {t('button_restore')}
                </Button>
              )}
              {canDelete && (
                <Button
                  variant='ghost'
                  iconStart='trash-2'
                  className='trash-page__selection-delete'
                  onClick={() =>
                    setConfirmDelete({
                      ids: [...selected.keys()],
                      name: onlySelected ? (trashRowName(onlySelected) ?? undefined) : undefined,
                    })
                  }
                >
                  {t('trash_action_delete_forever')}
                </Button>
              )}
            </div>
            <Button variant='ghost' onClick={() => setSelected(new Map())}>
              {t('button_clear')}
            </Button>
          </div>
        )}

        <DataTable
          columns={columns}
          rows={items}
          rowKey={(row) => row.id}
          // Selection feeds the bar's two verbs — without either it would
          // select into a bar of just count + Clear, so no checkboxes render.
          selectable={canRestore || canDelete}
          selectedIds={selectedIdSet}
          onToggleSelect={toggleSelect}
          allSelected={allSelected}
          someSelected={someSelected}
          onToggleSelectAll={toggleSelectAll}
          selectAllLabel={t('trash_select_all')}
          loading={isLoading || isFetching}
          error={isError}
          loadingText={t('text_loading')}
          errorText={t('error_generic')}
          minWidth={760}
          empty={
            <>
              <Icon name='trash-2' size={26} />
              {t('trash_empty')}
            </>
          }
        />

        <TableFooter
          page={page}
          limit={limit}
          total={total}
          onPage={setPage}
          onLimit={(n) => {
            setLimit(n);
            setPage(1);
          }}
          perPageLabel={t('trash_items_per_page')}
          rangeText={t('trash_range', { from, to, total })}
        />
      </Card>

      {/* One modal for both paths — the endpoint is bulk, so a single row and
          a whole selection are the same call; only the title differs. The copy
          is blunt on purpose: this skips the 30-day window for good. */}
      {confirmDelete && (
        <ConfirmModal
          open
          onClose={() => setConfirmDelete(null)}
          onConfirm={runDelete}
          tone='danger'
          icon='trash-2'
          title={
            confirmDelete.name
              ? t('trash_delete_confirm_title', { name: confirmDelete.name })
              : t('trash_bulk_delete_title', { count: confirmDelete.ids.length })
          }
          description={t('trash_delete_confirm_body')}
          confirmLabel={t('button_delete')}
          confirmVariant='danger'
          loading={deleting}
        />
      )}
    </div>
  );
};

export default TrashPage;
